import type { ReactNode } from 'react'

import { isNil } from 'ramda'

import { useBreakpoint } from '@twisto/hooks'
import { ChevronRightAltIcon } from '@twisto/icons'

import { BannerContextProvider } from './banner.context'
import * as styles from './banner.styles'
import { Action } from './components'

export type Size = 'medium' | 'small'
export type Variant = 'primary' | 'secondary' | 'warning' | 'error'

type Props = {
  children: ReactNode
  className?: string
  size?: Size
  variant?: Variant
  href?: string
  to?: string
  onClick?: () => void
}

export const Banner = ({
  children,
  className,
  size = 'medium',
  variant = 'primary',
  href,
  to,
  onClick,
}: Props) => {
  const hasAction = !isNil(href) || !isNil(to) || !isNil(onClick)
  const isDesktop = useBreakpoint('smUp')

  return (
    <BannerContextProvider hasAction={hasAction} size={size} variant={variant}>
      <Action data-testid="banner" href={href} to={to} onClick={onClick}>
        <article
          className={className}
          css={[
            styles.container,
            styles.containerSize[size],
            styles.containerBackground[variant],
            hasAction && isDesktop && styles.containerHover,
            hasAction && isDesktop && styles.containerBackgroundHover[variant],
          ]}
        >
          {children}
          {hasAction ? (
            <ChevronRightAltIcon
              className="banner-arrow"
              color="inherit"
              css={[
                styles.arrow,
                styles.arrowColor[variant],
                styles.arrowSize[size],
              ]}
            />
          ) : null}
        </article>
      </Action>
    </BannerContextProvider>
  )
}
